import { type RefObject, useEffect, useRef } from 'react'

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY
    if (o === 'auto' || o === 'scroll') return p
  }
  return null
}

// Keeps `endRef` in view as content grows, unless the user has scrolled up to read;
// scrolling back to the bottom re-pins it.
export function useStickToEnd(endRef: RefObject<HTMLElement | null>, dep: unknown) {
  const pinned = useRef(true)

  useEffect(() => {
    const el = endRef.current
    if (!el) return
    const parent = scrollParent(el)
    const box = () => parent ?? document.scrollingElement ?? document.documentElement
    let last = box().scrollTop
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = box()
      if (scrollHeight - scrollTop - clientHeight < 48) pinned.current = true
      else if (scrollTop < last) pinned.current = false
      last = scrollTop
    }
    const target: HTMLElement | Window = parent ?? window
    target.addEventListener('scroll', onScroll, { passive: true })
    return () => target.removeEventListener('scroll', onScroll)
  }, [endRef])

  useEffect(() => {
    if (!pinned.current) return
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    endRef.current?.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' })
  }, [dep, endRef])
}
